import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Anchor, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (loading) return null;
  if (user?.role === "admin") return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Signed in.");
      navigate("/admin/dashboard");
    } else {
      setErr(res.error || "Invalid credentials");
    }
  };

  return (
    <div className="min-h-screen flex bg-[#060A14] text-white">
      <div className="hidden lg:flex flex-1 bg-[#0C1322] items-end p-14 border-r border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#FF5722] flex items-center justify-center">
              <Anchor size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-lg">BILLTECH HOLDINGS</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Group Portal</div>
            </div>
          </div>
          <h2 className="font-display text-5xl leading-[0.95] tracking-tighter mb-4">
            Group Command<br />
            <span className="text-[#FF5722]">Portal.</span>
          </h2>
          <p className="text-white/60 text-sm max-w-md">
            Consolidated tender desk, career applications and contact routing for the Billtech
            Group of subsidiaries.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-[#FF5722]">
            <Lock size={12} /> Restricted Access
          </div>
          <h1 className="font-display text-3xl mb-2 tracking-tight">Sign in.</h1>
          <p className="text-white/60 text-sm mb-8">Administrator credentials required.</p>

          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="block text-[11px] font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Email</span>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={ADMIN.loginEmail}
                className="w-full bg-[#0C1322] border border-white/10 px-4 py-3 text-sm focus:border-[#FF5722] focus:ring-1 focus:ring-[#FF5722] outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Password</span>
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={ADMIN.loginPassword}
                className="w-full bg-[#0C1322] border border-white/10 px-4 py-3 text-sm focus:border-[#FF5722] focus:ring-1 focus:ring-[#FF5722] outline-none"
              />
            </label>

            {err && (
              <div className="text-sm text-[#EF4444] font-mono" data-testid="admin-login-error">
                {err}
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid={ADMIN.loginSubmit}>
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
